import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashGroupBy from 'lodash/fp/groupBy';

import {
  SR_API_ACTION_APARTMENT_DELETE,
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_APARTMENTS,
  DATA_TABLE_VIEW_ID_APARTMENTS_ALL,
} from 'common/dataTableConstants';

import {
  LISTING_STATUS_ACTIVE,
} from 'common/listingConstants';

import {
  dataTable,
  actionDataTableReset,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';

import {
  getListingFullAddressString,
} from 'common/listingHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrTable from 'client/components/SrTable';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrAdminPhotosEditor, {
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT,
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT_COMMUNITY_PHOTOS,
} from 'client/components/SrAdminPhotosEditor';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import postWithState from 'client/actions/postWithState';

function getDataTableSettings() {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_APARTMENTS,
    viewId: DATA_TABLE_VIEW_ID_APARTMENTS_ALL,
    sort: {
      'apartments.createdDate': 'desc',
    },
    count: 100,
  };

  return dataTableSettings;
}

function getShowUnitsColumn({ floorPlans, listings }) {
  if (
    listings.filter(({ status }) => status === LISTING_STATUS_ACTIVE).length === 0
    || floorPlans.length === 0
  ) {
    return {
      content: 0,
    };
  }

  const tourableApartmentUnitsCount = floorPlans.reduce((memo, floorPlan) => {
    memo += (floorPlan.tourableApartmentUnits || []).length;
    return memo;
  }, 0);

  const unitsByFloorPlanId = lodashGroupBy('floorPlanId', listings);

  const floorPlanWithActiveUnitNoShowUnit = floorPlans.find((floorPlan) => {
    const activeUnits = (unitsByFloorPlanId[floorPlan.id] || [])
      .filter((unit) => unit.status === LISTING_STATUS_ACTIVE);

    if (!activeUnits.length) {
      return false;
    }

    return !activeUnits.some((unit) => floorPlan.tourableApartmentUnits.includes(unit.apartmentUnit));
  });

  return {
    style: (!tourableApartmentUnitsCount || floorPlanWithActiveUnitNoShowUnit)
      ? { backgroundColor: 'red', color: 'white' }
      : {},
    content: tourableApartmentUnitsCount || 'No Show Units',
  };
}

@dataTable(getDataTableSettings)
@withInfiniteScroll({
  offset: 420,
})
class SrDataTableApartments extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);
    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
    this.state = {
      photoCoverEditorModalApartmentId: null,
      photoCommunityEditorModalApartmentId: null,
    };
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettings(this.props));
    }
  }

  _onDeleteApartment = (e) => {
    const { dispatch, items } = this.props;
    const apartmentId = parseInt(e.target.dataset.id, 10);
    const apartment = items.find(({ id }) => id === apartmentId);

    if (!global.confirm(`Are you sure you want to delete the apartment "${apartment.name}"?`)) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_DELETE,
      payload: {
        apartmentId,
      },
      onSuccess: () => {
        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_APARTMENTS,
        });
      },
      onError: (err) => {
        global.alert(`Error: "${err.message}"`);
      },
    });
  }

  _onToggleCoverPhotoEditorModal = (apartmentId) => {
    this.setState({
      photoCoverEditorModalApartmentId: apartmentId || null,
    });
  }

  _onApartmentCoverPhotosEdit = ({ coverPhotoEditorModalApartment, newPhotoUrls }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'apartments',
        where: {
          id: coverPhotoEditorModalApartment.id,
        },
        updates: {
          photoUrls: newPhotoUrls,
        },
      },
      onSuccess: () => {
        this.setState({
          photoCoverEditorModalApartmentId: null,
        });
        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_APARTMENTS,
        });
      },
    });
  }

  _onToggleCommunityPhotoEditorModal = (apartmentId) => {
    this.setState({
      photoCommunityEditorModalApartmentId: apartmentId || null,
    });
  }

  _onApartmentCommunityPhotosEdit = ({ communityPhotoEditorModalApartment, newPhotoUrls }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'apartments',
        where: {
          id: communityPhotoEditorModalApartment.id,
        },
        updates: {
          communityPhotoUrls: newPhotoUrls,
        },
      },
      onSuccess: () => {
        this.setState({
          photoCommunityEditorModalApartmentId: null,
        });
        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_APARTMENTS,
        });
      },
    });
  }

  render() {
    const {
      items,
      dataTableApiState,
      onInfiniteScrollWrapperRef,
    } = this.props;

    const {
      photoCoverEditorModalApartmentId,
      photoCommunityEditorModalApartmentId,
    } = this.state;

    const coverPhotoEditorModalApartment = photoCoverEditorModalApartmentId &&
      items.find(({ id }) => id === photoCoverEditorModalApartmentId);
    const communityPhotoEditorModalApartment = photoCommunityEditorModalApartmentId &&
      items.find(({ id }) => id === photoCommunityEditorModalApartmentId);

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrTable
          headers={[
            { content: 'id' },
            { content: 'apartment name' },
            { content: 'partner name' },
            { content: 'units' },
            { content: 'active units' },
            { content: 'unlinked units' },
            { content: 'show units' },
            { content: 'apartment buildings' },
            { content: 'cover photos' },
            { content: 'community photos' },
            { content: 'options' },
          ]}
          rows={items.map(({ partner, apartmentBuildings, floorPlans, listings, ...apartment }) => ({
            cells: [
              {
                content: apartment.id,
              },
              {
                content: (
                  <SrBox>
                    <SrText>
                      {apartment.name}
                    </SrText>
                    <SrBox>
                      <SrLink color='blue300' target='_blank' to={`/apartment/${apartment.id}`}>
                        Renter
                      </SrLink>
                      <SrBox display='inlineBlock' width={10} />
                      <SrLink
                        color='blue300'
                        target='_blank'
                        to={`/partner/${partner.slug}/apartment/${apartment.id}`}
                      >
                        Partner
                      </SrLink>
                    </SrBox>
                  </SrBox>
                ),
              },
              { content: partner ? (
                <SrLink color='blue300' target='_blank' to={`/partner/${partner.slug}`}>
                  {partner.displayName}
                </SrLink>
              ) : '--' },
              { content: apartment.numberOfUnits || '--' },
              { content: apartment.activeListingsCount || '--' },
              {
                content: floorPlans.length ? listings.filter((listing) => !listing.floorPlanId).length : '--',
                style: floorPlans.length && listings.filter((listing) => !listing.floorPlanId).length
                  ? { backgroundColor: 'red', color: 'white' }
                  : {},
              },
              getShowUnitsColumn({ floorPlans, listings }),
              {
                content: apartmentBuildings.map((apartmentBuilding) => (
                  <div key={apartmentBuilding.id}>
                    <SrText>
                      {getListingFullAddressString(apartmentBuilding)}
                    </SrText>
                  </div>
                )),
              },
              {
                content: (
                  <SrText
                    onClick={() => this._onToggleCoverPhotoEditorModal(apartment.id)}
                    color='blue300'
                    cursor='pointer'
                  >
                    Edit cover photos
                  </SrText>
                ),
              },
              {
                content: (
                  <SrText
                    onClick={() => this._onToggleCommunityPhotoEditorModal(apartment.id)}
                    color='blue300'
                    cursor='pointer'
                  >
                    Edit community photos
                  </SrText>
                ),
              },
              {
                content: (
                  <SrBox>
                    <SrLink color='blue300' to={`/admin/apartments/${apartment.id}`}>
                      Edit
                    </SrLink>
                    <SrBox display='inlineBlock' width={10} />
                    <SrText
                      cursor='pointer'
                      color='blue300'
                      data-id={apartment.id}
                      onClick={this._onDeleteApartment}
                    >
                      Delete
                    </SrText>
                  </SrBox>
                ),
              },
            ],
          }))}
        />
        {dataTableApiState.inProgress && (
          <div className='loading-ellipses left' />
        )}
        {coverPhotoEditorModalApartment && (
          <SrDarkModal onClose={this._onToggleCoverPhotoEditorModal}>
            <SrModalContent style={{ maxWidth: 440, height: '94vh', marginTop: 4 }}>
              <SrAdminPhotosEditor
                filePath={SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT}
                photoUrls={coverPhotoEditorModalApartment.photoUrls}
                onSave={(newPhotoUrls) => {
                  this._onApartmentCoverPhotosEdit({
                    coverPhotoEditorModalApartment,
                    newPhotoUrls,
                  });
                }}
              />
            </SrModalContent>
          </SrDarkModal>
        )}
        {communityPhotoEditorModalApartment && (
          <SrDarkModal onClose={this._onToggleCommunityPhotoEditorModal}>
            <SrModalContent style={{ maxWidth: 440, height: '94vh', marginTop: 4 }}>
              <SrAdminPhotosEditor
                filePath={SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT_COMMUNITY_PHOTOS}
                photoUrls={communityPhotoEditorModalApartment.communityPhotoUrls}
                onSave={(newPhotoUrls) => {
                  this._onApartmentCommunityPhotosEdit({
                    communityPhotoEditorModalApartment,
                    newPhotoUrls,
                  });
                }}
              />
            </SrModalContent>
          </SrDarkModal>
        )}
      </div>
    );
  }
}

SrDataTableApartments.displayName = 'SrDataTableApartments';

export default SrDataTableApartments;
